import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaProjectDiagram,
  FaRupeeSign,
  FaUsers,
  FaClipboardList,
  FaPlus,
  FaChartLine,
  FaBell,
} from "react-icons/fa";
import { MdDashboard } from "react-icons/md";
import useAuth from "../useAuth";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

export default function Homepage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchSchemes = async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        pageNo: 1,
        pageSize: 10,
      };

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(data?.data || []);
      setSchemeStats({
        totalSchemes: data?.totalSchemes,
        totalDraftSchemes: data?.totalDraftSchemes,
        totalCompletedSchemes: data?.totalCompletedSchemes,
        totalAllocationAmount: data?.totalAllocationAmount,
      });
    } catch (error) {
      console.error("Error fetching schemes:", error);
      toast.error("Failed to fetch schemes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 120 },
    },
  };

  const stats = [
    {
      title: "Total Schemes",
      value: schemeStats?.totalSchemes,
      icon: <FaProjectDiagram className="text-xl text-blue-600" />,
      bg: "bg-blue-50",
      desc: "+12% from last month",
    },
    {
      title: "Completed Schemes",
      value: schemeStats?.totalCompletedSchemes,
      icon: <FaRupeeSign className="text-xl text-blue-600" />,
      bg: "bg-blue-50",
      desc: "Allocated this fiscal year",
    },
    {
      title: "Draft Schemes",
      value: schemeStats?.totalDraftSchemes,
      icon: <FaUsers className="text-xl text-blue-600" />,
      bg: "bg-blue-50",
      desc: "Active beneficiaries",
    },
    {
      title: "Total Fund Allocation",
      value: `₹${schemeStats?.totalAllocationAmount?.toLocaleString("en-In")}`,
      icon: <FaClipboardList className="text-xl text-blue-600" />,
      bg: "bg-blue-50",
      desc: "Requires attention",
    },
  ];

  const quickActions = [
    {
      title: "Add New Scheme",
      icon: <FaPlus />,
      action: () => navigate("/my/addNewScheme"),
      color: "bg-blue-100 text-blue-700 hover:bg-blue-200",
    },
    {
      title: "View Reports",
      icon: <FaChartLine />,
      action: () => {},
      color: "bg-slate-100 text-slate-700 hover:bg-slate-200",
    },
    {
      title: "Notifications",
      icon: <FaBell />,
      action: () => {},
      color: "bg-amber-100 text-amber-700 hover:bg-amber-200",
    },
  ];

  const recentSchemes = [
    {
      name: "Rural Road Development",
      status: "Active",
      date: "2024-03-15",
      budget: "₹ 2.5 Cr",
    },
    {
      name: "Water Supply Project",
      status: "Pending",
      date: "2024-03-14",
      budget: "₹ 1.2 Cr",
    },
    {
      name: "Solar Street Lights",
      status: "Completed",
      date: "2024-03-10",
      budget: "₹ 0.8 Cr",
    },
    {
      name: "School Renovation",
      status: "Active",
      date: "2024-03-08",
      budget: "₹ 1.5 Cr",
    },
  ];

  return (
    <motion.div
      className="p-6 min-h-screen bg-[#f7f9fc]"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <MdDashboard className="text-blue-600" /> Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back,{" "}
            <span className="font-semibold text-slate-700">
              {user?.name || "Admin"}
            </span>
          </p>
        </div>

        <div className="mt-4 md:mt-0 text-right">
          <div className="text-sm font-medium text-slate-600">
            {currentDate.toLocaleDateString("en-IN", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <div className="text-xs text-slate-400">
            {currentDate.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            className={`rounded-xl p-6 shadow-md border border-slate-200 bg-white hover:shadow-lg transition`}
          >
            <div className="flex justify-between">
              <div>
                <p className="text-slate-500 text-sm">{stat.title}</p>
                <h3 className="text-3xl font-bold mt-2 text-slate-800">
                  {stat.value}
                </h3>
              </div>
              <div className="p-3 rounded-lg bg-blue-50">{stat.icon}</div>
            </div>

            <p className="mt-4 text-xs text-slate-500">{stat.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Section */}
        <motion.div
          variants={itemVariants}
          className="lg:col-span-2 bg-white rounded-xl shadow-md border border-slate-200 p-6"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-800">Recent Schemes</h2>
            <button
              onClick={() => navigate("/my/schemesList")}
              className="text-sm text-blue-600 font-medium hover:underline"
            >
              View All
            </button>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 text-xs uppercase border-b">
                <th className="pb-3 text-left">Scheme Name</th>
                <th className="pb-3 text-left">Status</th>
                <th className="pb-3 text-left">Sector</th>
                <th className="pb-3 text-right">Allocated Amount</th>
              </tr>
            </thead>

            <tbody className="text-slate-700">
              {schemes.map((scheme, index) => (
                <tr
                  key={index}
                  className="border-b hover:bg-slate-50 cursor-pointer"
                  onClick={() => {
                    scheme?.formStatus === "completed"
                      ? navigate("../schemeDetails", {
                          state: { schemeId: scheme?.id },
                        })
                      : navigate("../addNewScheme", {
                          state: {
                            draftSchemeId: scheme?.id,
                            draftSchemeData: scheme,
                          },
                        });
                  }}
                >
                  <td className="py-3">{scheme.schemeName || "---"}</td>
                  <td>
                    <span
                      className={`px-2 py-1 rounded-full text-xs capitalize ${
                        scheme.formStatus === "completed"
                          ? "bg-green-100 text-green-700"
                          : scheme.formStatus === "draft" &&
                            "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {scheme?.formStatus}
                    </span>
                  </td>
                  <td className="text-slate-500">{scheme?.sector}</td>
                  <td className="text-right font-semibold">
                    ₹{scheme?.allocatedAmount?.toLocaleString("en-In")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Right Section */}
        <motion.div variants={itemVariants} className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4">
              Quick Actions
            </h2>

            <div className="space-y-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`w-full flex items-center p-3 rounded-lg transition-all ${action.color}`}
                >
                  <div className="p-2 bg-white rounded-md shadow">
                    {action.icon}
                  </div>
                  <span className="ml-3 font-medium">{action.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-blue-600 rounded-xl shadow-md p-6 text-white">
            <h3 className="text-lg font-bold mb-2 text-white">Need Help?</h3>
            <p className="text-blue-100 text-sm mb-4">
              Need assistance understanding dashboard modules?
            </p>
            <button className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg">
              Contact Support
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
